import React, { useState, useEffect } from 'react';
import { X, Trash2 } from 'lucide-react';

const EdgeSidebar = ({ 
  edge, 
  onUpdate, 
  onDelete, 
  onClose, 
  loading = false,
  botStatus 
}) => {
  const [label, setLabel] = useState('');

  useEffect(() => {
    if (edge) {
      setLabel(edge.data?.label || '');
    }
  }, [edge]);

  const isReadOnly = botStatus !== '10001'; // Only allow editing for draft bots

  const handleSave = () => {
    if (edge) {
      onUpdate(edge.id, { 
        label: label.trim() || 'Unnamed'
      });
      onClose();
    }
  };

  const handleDelete = () => {
    if (edge && window.confirm('Are you sure you want to delete this connection?')) {
      onDelete(edge.id);
      onClose();
    }
  };

  if (!edge) return null;

  return (
    <div className="w-80 bg-white shadow-lg border-l border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Edit Connection</h3>
          {isReadOnly && (
            <p className="text-sm text-gray-500 italic">
              {botStatus === '10002' ? 'Read-only: Published bot' : 
               botStatus === '10003' ? 'Read-only: Archived bot' : 'Read-only'}
            </p>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded-md transition-colors"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 p-4 space-y-6">
        {/* Connection Info */}
        <div className="bg-gray-50 p-3 rounded-lg">
          <div className="text-sm text-gray-600 mb-1">From Node</div>
          <div className="font-medium text-gray-800">{edge.source}</div>
        </div>

        <div className="bg-gray-50 p-3 rounded-lg">
          <div className="text-sm text-gray-600 mb-1">To Node</div>
          <div className="font-medium text-gray-800">{edge.target}</div>
        </div>

        {/* Edge Label */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Connection Label
          </label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Enter connection label (e.g., 'Yes', 'No', 'Continue')"
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              isReadOnly ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'
            }`}
            disabled={loading || isReadOnly}
          />
          <p className="text-xs text-gray-500 mt-1">
            This label will appear on the connection line
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="p-4 border-t border-gray-200 space-y-3">
        <button
          onClick={handleSave}
          disabled={loading || isReadOnly}
          className={`w-full px-4 py-2 rounded-md transition-colors flex items-center justify-center ${
            isReadOnly 
              ? 'bg-gray-400 cursor-not-allowed text-gray-200' 
              : 'bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white'
          }`}
          title={isReadOnly ? 'Cannot save non-draft bot' : 'Save changes'}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </button>

        <button
          onClick={handleDelete}
          disabled={loading || isReadOnly}
          className={`w-full px-4 py-2 rounded-md transition-colors flex items-center justify-center ${
            isReadOnly 
              ? 'bg-gray-400 cursor-not-allowed text-gray-200' 
              : 'bg-red-500 hover:bg-red-600 disabled:bg-gray-300 text-white'
          }`}
          title={isReadOnly ? 'Cannot delete from non-draft bot' : 'Delete connection'}
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Delete Connection
        </button>
      </div>
    </div>
  );
};

export default EdgeSidebar;